import type {FC, ReactElement} from 'react';
import {Link} from 'react-router-dom';
import {useEffect, useState, useRef} from 'react';
import {usePlayback} from 'contexts/WatchContext';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';
import {Avatar, Box, Button, IconButton, Typography, useMediaQuery, useTheme} from '@mui/material';
import { getColorFromHash } from 'utils/ColorUtils';
import { getOwnerMSizeThumb } from 'utils/VideoUtils';
import { useTranslation } from 'react-i18next';
import { getVideoTitle } from 'utils/PromoUtils';
import { NoSnippet } from 'utils/NoSnippet';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import Badge from '@mui/material/Badge';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MenuIcon from '@mui/icons-material/Menu';
import HeartIcon from 'components/HeartIcon';
import ShareIcon from '@mui/icons-material/Share';
import ShareMenu from './ShareMenu';

/**
 * Props for WatchSubject
 */
export interface WatchSubjectProps {
  video: VideoSubjectMetadataMessage;
  titleLink: { to: string; state?: any };
  ownerLink: { to: string };
  onPlayPrevVideo(): void;
  onPlayNextVideo(): void;
  onVideoFeedback(video: VideoSubjectMetadataMessage, saved: boolean): void;
}

/**
 * WatchSubject displays the main video title, metadata, and controls
 */

const WatchSubject: FC<WatchSubjectProps> = ({
  video,
  titleLink,
  ownerLink,
  onPlayPrevVideo,
  onPlayNextVideo,
  onVideoFeedback
}): ReactElement => {
  const { isPlaying, toggle, drawerOpen, openDrawer, closeDrawer, facadeQueue } = usePlayback();
  // Local saved state for immediate heart toggle
  // Reset saved status when the video or its saved flag changes
  const [saved, setSaved] = useState<boolean>(video.videoHeart > 0);
  useEffect(() => {
    setSaved(video.videoHeart > 0);
  }, [video.musicVideo, video.videoHeart]);
  const handleToggleSave = (): void => {
    const next = !saved;
    setSaved(next);
    onVideoFeedback(video, next);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // i18n for UI strings and promoSubject namespace for share title
  const { t, i18n } = useTranslation();
  const { t: tPromo } = useTranslation('promoSubject');
  const currentLang = (i18n.language || 'en').split('-')[0];
  // Build share URL with active language code
  const shareUrl = `https://title.fm/lnk/public/video/${video.musicVideo}/${currentLang}`;
  // Localized share title via promo utils
  const shareTitle = getVideoTitle(
    tPromo,
    video.trackTitle,
    video.ownerTitle,
    video.titleCount
  );

  /**
   * Approximate view counts with K/M/B suffixes
   * - <500 : raw number
   * - <500K: decimal in K, to 2 decimals
   * - <500M: decimal in M, to 2 decimals
   * - else : decimal in B, to 2 decimals
   */
  const approxViews = (n: number): string => {
    if (n < 500) {
      return n.toString();
    }
    if (n < 500000) {
      return `${(n / 1000).toFixed(2)}K`;
    }
    if (n < 500000000) {
      return `${(n / 1000000).toFixed(2)}M`;
    }
    return `${(n / 1000000000).toFixed(2)}B`;
  };
  // Badge state: fixed color cycle when first queue item changes
  // Badge count for queue items
  const badgeCount = facadeQueue.length;
  const badgeColors = [theme.palette.secondary.main, theme.palette.info.main];
  const [badgeColorIdx, setBadgeColorIdx] = useState(0);
  const prevFirstKey = useRef<string | undefined>(undefined);
  const firstKey = facadeQueue[0]?.musicVideo;
  useEffect(() => {
    if (firstKey && firstKey !== prevFirstKey.current) {
      setBadgeColorIdx(i => (i + 1) % badgeColors.length);
    }
    prevFirstKey.current = firstKey;
  }, [firstKey]);
  const badgeColor = badgeColors[badgeColorIdx];
  return (
  <Box sx={{ px: { xs: 1, md: 0 }, pt: isMobile ? 0 : 1 }}>
      {/* Video title and view count */}
      <Box sx={{ mt: isMobile ? 0 : 1, minWidth: 0 }}>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          color="text.primary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          <NoSnippet component="span">
          <Box
            component={Link}
            to={titleLink.to}
            state={titleLink.state}
            fontWeight="fontWeightMedium"
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {video.trackTitle}
          </Box>
          <Box component="span" sx={{ mx: 0.5 }}>
            ·
          </Box>
          </NoSnippet>
          <Box component="span" fontWeight="fontWeightLight" color="text.secondary">
            {video.videoTitle}
          </Box>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          {approxViews(video.videoViews)} {t('views')}
        </Typography>
      </Box>
      <NoSnippet>
      {/* Owner info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 0 }}>
      <Avatar
        component={Link}
        to={ownerLink.to}
        src={getOwnerMSizeThumb(video)}
        alt={video.ownerTitle}
        sx={{
            width: 40,
            height: 40,
            cursor: 'pointer',
            textDecoration: 'none',
            bgcolor: getColorFromHash(video.ownerTitle),
            color: 'text.primary',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            component={Link}
            to={ownerLink.to}
            noWrap
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightMedium,
              textDecoration: 'none',
              color: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            })}
          >
            {video.ownerTitle}
          </Typography>
        </Box>
      </Box>
      {/* Row 3: Action buttons & play controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
         {/* Action buttons */}
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <>
            {/* Share menu with social buttons and copy link */}
            <ShareMenu url={shareUrl} title={shareTitle}>
              {isMobile ? (
                <Button
                  size="medium"
                  startIcon={<ShareIcon sx={{ color: 'inherit' }} />}
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    borderRadius: '9999px',
                    textTransform: 'none',
                    px: 2,
                    '&:hover': { bgcolor: 'grey.200' },
                  }}
                >
                  {t('share').toUpperCase()}
                </Button>
              ) : (
                <Button
                  startIcon={<ShareIcon sx={{ color: 'inherit' }} />}
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    borderRadius: '9999px',
                    textTransform: 'none',
                    px: 6,
                    '&:hover': { bgcolor: 'grey.200' },
                  }}
                >
                  {t('share').toUpperCase()}
                </Button>
              )}
            </ShareMenu>
            <IconButton
              size="small"
              onClick={handleToggleSave}
              sx={{ color: saved ? 'error.main' : undefined }}
              aria-label={saved ? 'Unlike' : 'Like'}
            >
              <HeartIcon filled={saved} size={24} />
            </IconButton>
          </>
        </Box>
        {/* Play controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={onPlayPrevVideo}
            aria-label="Previous"
          >
            <SkipPreviousIcon />
          </IconButton>
          {/* Mobile: queue toggle with badge; Desktop: play/pause */}
          {isMobile ? (
            badgeCount > 0 && !drawerOpen ? (
              <Badge
                badgeContent={badgeCount}
                max={100}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ '& .MuiBadge-badge': { bgcolor: badgeColor, color: theme.palette.common.white } }}
              >
                <IconButton
                  size="small"
                  disabled={drawerOpen}
                  onClick={() => { drawerOpen ? closeDrawer() : openDrawer(); }}
                  aria-label={drawerOpen ? 'Close queue' : 'Open queue'}
                >
                  <MenuIcon />
                </IconButton>
              </Badge>
            ) : (
              <IconButton
                size="small"
                disabled={drawerOpen}
                onClick={() => { drawerOpen ? closeDrawer() : openDrawer(); }}
                aria-label={drawerOpen ? 'Close queue' : 'Open queue'}
              >
                <MenuIcon />
              </IconButton>
            )
          ) : (
            <IconButton
              size="small"
              onClick={() => toggle()}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={onPlayNextVideo}
            aria-label="Next"
          >
            <SkipNextIcon />
          </IconButton>
        </Box>
      </Box>
      </NoSnippet>
      {/* Divider between action buttons and tabs in mobile mode */}
      {isMobile && <Box sx={{ mt: 2, mb: 0 }} />}
    </Box>
  );
};

export default WatchSubject;